/**
 * Color Mode Context
 * Manages theme preference (light/dark/system) with localPreferences persistence
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useColorScheme } from 'react-native';
import {
  getLocalPreferences,
  isLocalPreferencesLoaded,
  loadLocalPreferences,
  updateLocalPreferences,
} from '../services/localPreferences';
import type { ColorModePreference } from '../services/localPreferences';

// Re-export so existing consumers keep working
export type { ColorModePreference };
export type EffectiveColorMode = 'light' | 'dark';

interface ColorModeContextValue {
  /** User's preference: light, dark, or system */
  colorModePreference: ColorModePreference;
  /** The actual mode being used (resolved from system if preference is 'system') */
  effectiveColorMode: EffectiveColorMode;
  /** Whether dark mode is currently active */
  isDark: boolean;
  /** Update the color mode preference */
  setColorModePreference: (mode: ColorModePreference) => void;
}

const ColorModeContext = createContext<ColorModeContextValue | undefined>(undefined);

interface ColorModeProviderProps {
  children: React.ReactNode;
  /** Optional initial mode — skips localPreferences load when provided (used by Storybook) */
  initialMode?: ColorModePreference;
}

export function ColorModeProvider({ children, initialMode }: ColorModeProviderProps) {
  const systemColorScheme = useColorScheme();
  const [colorModePreference, setColorModePreferenceState] = useState<ColorModePreference>(initialMode ?? 'system');
  // When initialMode is provided (Storybook + perf tests), there is nothing to
  // load, so skip the null-render gate below — otherwise the mount effect flips
  // isLoaded false→true and forces a second commit (harness double-render).
  const [isLoaded, setIsLoaded] = useState(initialMode != null);

  // Load persisted preference on mount (skip when Storybook controls the mode)
  useEffect(() => {
    if (initialMode) {
      setIsLoaded(true);
      return;
    }

    const load = async () => {
      try {
        const prefs = isLocalPreferencesLoaded()
          ? getLocalPreferences()
          : await loadLocalPreferences();
        const stored = prefs.colorMode;
        if (stored && ['light', 'dark', 'system'].includes(stored)) {
          setColorModePreferenceState(stored);
        }
      } catch {
        // Ignore errors, use default
      } finally {
        setIsLoaded(true);
      }
    };

    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: initialMode is handled by the sync effect below
  }, []);

  // Sync when initialMode changes externally (e.g., Storybook globals toggle)
  useEffect(() => {
    if (initialMode) {
      setColorModePreferenceState(initialMode);
    }
  }, [initialMode]);

  // Update preference and persist
  const setColorModePreference = useCallback((mode: ColorModePreference) => {
    setColorModePreferenceState(mode);
    updateLocalPreferences({ colorMode: mode }).catch(() => {
      // Ignore storage errors
    });
  }, []);

  // Resolve effective color mode
  const effectiveColorMode: EffectiveColorMode = useMemo(() => {
    if (colorModePreference === 'system') {
      return systemColorScheme === 'dark' ? 'dark' : 'light';
    }
    return colorModePreference;
  }, [colorModePreference, systemColorScheme]);

  const isDark = effectiveColorMode === 'dark';

  const value = useMemo(
    () => ({
      colorModePreference,
      effectiveColorMode,
      isDark,
      setColorModePreference,
    }),
    [colorModePreference, effectiveColorMode, isDark, setColorModePreference]
  );

  // Don't render children until we've loaded the preference
  // This prevents a flash of wrong theme
  if (!isLoaded) {
    return null;
  }

  return (
    <ColorModeContext.Provider value={value}>
      {children}
    </ColorModeContext.Provider>
  );
}

export function useColorMode(): ColorModeContextValue {
  const context = useContext(ColorModeContext);
  if (!context) {
    throw new Error('useColorMode must be used within a ColorModeProvider');
  }
  return context;
}
